import { useCallback, useRef } from "react";
import { useAnalytics } from "./analytics-provider";
import type { TrackEvent } from "@salestrak/analytics-taxonomy";

/**
 * Wraps an Apollo mutation function so the analytics event fires only after
 * the mutation resolves — failed mutations are not counted as completed
 * actions. The event is read through a ref so inline literals don't break
 * the returned function's identity.
 *
 * Generic over the function type, not over Apollo's TData/TVars pair: the
 * wrapper never inspects the options, and restating the mutate signature
 * here would couple it to Apollo's generics (which carry the declared
 * errorPolicy as a type parameter) for no gain.
 */
export function useTrackedMutation<TArgs extends unknown[], TResult>(
  mutationFn: (...args: TArgs) => Promise<TResult>,
  event: TrackEvent,
): (...args: TArgs) => Promise<TResult> {
  const { track } = useAnalytics();
  const eventRef = useRef(event);
  eventRef.current = event;

  return useCallback(
    async (...args: TArgs) => {
      const result = await mutationFn(...args);
      track(eventRef.current);
      return result;
    },
    [mutationFn, track],
  );
}
